import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError, type EmailRecipient } from '../api/client'
import './EmailReport.css'

/**
 * Emailing a compliance report to somebody.
 *
 * Replaces a `mailto:` link, which could carry text and nothing else — no
 * attachment, no record that it happened, and no way to know whether the address
 * was even typed correctly. This sends from the server: a PDF is built and
 * attached, the figures appear in the body, and the delivery is recorded.
 *
 * Three things the design turns on:
 *
 * **The address is the dangerous field.** A compliance report is a disclosure, and
 * a typo sends one company's fleet position to a stranger. So past recipients are
 * offered back — from the send history, which is the only list that cannot drift —
 * and the address is shown back before sending rather than assumed.
 *
 * **It says when it cannot send.** Without a relay the button is disabled and
 * explains why, instead of failing after the user has typed everything.
 *
 * **It reports what actually happened.** A failure names the reason, because "did
 * that report go out" is a question somebody will be asked.
 */
export function EmailReport({
  vehicleIds,
  label,
  onDone,
}: {
  vehicleIds: number[]
  /** What is being sent, for the heading — "MH12EQ3823" or "12 vehicles". */
  label: string
  onDone?: () => void
}) {
  const [address, setAddress] = useState('')
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [showList, setShowList] = useState(false)

  const inputId = useId()
  const boxRef = useRef<HTMLDivElement>(null)

  const status = useQuery({
    queryKey: ['email', 'status'],
    queryFn: api.getEmailStatus,
    staleTime: 5 * 60_000,
    retry: false,
  })

  // Every past recipient, filtered in the browser. The list is small — this is a
  // handful of colleagues and insurers, not a CRM — and filtering locally means
  // the suggestions keep up with typing instead of trailing a request per
  // keystroke.
  const recipients = useQuery({
    queryKey: ['email', 'recipients'],
    queryFn: () => api.getEmailRecipients(undefined, 50),
    staleTime: 60_000,
    retry: false,
  })

  const suggestions = useMemo(() => {
    const all = recipients.data ?? []
    const typed = address.trim().toLowerCase()
    if (!typed) return all.slice(0, 6)

    return all
      .filter((r) => (r.toAddress ?? '').includes(typed) || (r.toName ?? '').toLowerCase().includes(typed))
      .slice(0, 6)
  }, [recipients.data, address])

  // Close the suggestion list on an outside click, so it does not sit over the
  // Send button waiting to swallow it.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowList(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const send = useMutation({
    mutationFn: () =>
      api.sendReportEmail({
        toAddress: address.trim(),
        toName: name.trim() || null,
        vehicleIds,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      // The list has a new address on it now.
      void recipients.refetch()
    },
  })

  const configured = status.data?.isConfigured ?? false

  // Deliberately shallow, and it mirrors the server's rule rather than being
  // stricter. Full RFC validation rejects addresses that work; the relay is the
  // only thing that truly knows, and a bounce is recorded either way.
  const looksValid = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(address.trim())

  const canSend = configured && looksValid && vehicleIds.length > 0 && !send.isPending

  function choose(r: EmailRecipient) {
    setAddress(r.toAddress ?? '')
    setName(r.toName ?? '')
    setShowList(false)
  }

  if (send.isSuccess) {
    return (
      <div className="mailr mailr--done">
        <p className="mailr__ok">
          <strong>Sent to {address.trim()}.</strong>
        </p>
        <p className="mailr__note">
          The report went as a PDF attachment, with the figures in the message itself.
          It is on the send history.
        </p>
        <div className="mailr__actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              send.reset()
              setNote('')
            }}
          >
            Send to somebody else
          </button>
          {onDone && (
            <button type="button" className="btn btn--primary" onClick={onDone}>
              Done
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <form
      className="mailr"
      onSubmit={(e) => {
        e.preventDefault()
        if (canSend) send.mutate()
      }}
    >
      {/* Said before anything is typed, not after Send is pressed. */}
      {status.isFetched && !configured && (
        <p className="mailr__msg mailr__msg--warn">
          {status.data?.message ?? 'Reports cannot be emailed yet — no mail relay is configured.'}
        </p>
      )}

      <div className="mailr__field" ref={boxRef}>
        <label className="eyebrow" htmlFor={inputId}>
          Send to
        </label>

        <input
          id={inputId}
          className="mailr__input"
          type="email"
          inputMode="email"
          autoComplete="off"
          placeholder="name@company.com"
          value={address}
          disabled={!configured}
          onChange={(e) => {
            setAddress(e.target.value)
            setShowList(true)
          }}
          onFocus={() => setShowList(true)}
        />

        {/*
          Past recipients. A custom list rather than <datalist>, which cannot show
          the name beside the address and renders differently in every browser —
          and the name is what tells two similar addresses apart.
        */}
        {showList && suggestions.length > 0 && (
          <ul className="mailr__sugg">
            {suggestions.map((r) => (
              <li key={r.toAddress}>
                <button type="button" onClick={() => choose(r)}>
                  <span className="mailr__sa">{r.toAddress}</span>
                  {r.toName && <span className="mailr__sn">{r.toName}</span>}
                  <span className="mailr__sc">
                    {r.sendCount === 1 ? 'once' : `${r.sendCount}×`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {address.trim() !== '' && !looksValid && (
          <p className="mailr__hint mailr__hint--warn">That does not look like an email address.</p>
        )}
      </div>

      <div className="mailr__field">
        <label className="eyebrow" htmlFor={`${inputId}-name`}>
          Their name <span className="mailr__opt">optional</span>
        </label>
        <input
          id={`${inputId}-name`}
          className="mailr__input"
          value={name}
          disabled={!configured}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="mailr__field">
        <label className="eyebrow" htmlFor={`${inputId}-note`}>
          Note <span className="mailr__opt">optional</span>
        </label>
        <input
          id={`${inputId}-note`}
          className="mailr__input"
          placeholder="Please renew before the audit."
          value={note}
          disabled={!configured}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="mailr__hint">Appears at the top of the message.</p>
      </div>

      <p className="mailr__what">
        <strong>{label}</strong> will be attached as a PDF, with the figures in the message
        itself.
        {status.data?.fromAddress && <> Sent from {status.data.fromAddress}.</>}
      </p>

      {send.isError && (
        <p className="mailr__msg mailr__msg--bad">
          {(send.error as ApiError).message || 'The message could not be sent.'}
        </p>
      )}

      <div className="mailr__actions">
        <button type="submit" className="btn btn--primary" disabled={!canSend}>
          {send.isPending ? 'Sending…' : 'Send report'}
        </button>
        {onDone && (
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
